import { bullhorn, jobAdder } from './constants';
import { conditionallyUpdateCacheAndGetSelectedSubCustomerId } from './session';
import { bullhornRestApiUrl } from '../config.local';
import { statuses } from '../lib/ApiBase';
import { UserIntegration, getIntegrationsByCustomerId, refreshIntegrationToken } from './integrations';
import { GetJobAdderAccessTokenFromAuthCode, getJobAdderApplication, getJobAdderCandidate } from './api';
import { hasValue } from '../lib/utils';

export interface BhToken {
	BhRestToken: string;
	restUrl: string;
}

export const createCertification = async (
	integrationName: string,
	userIntegration: UserIntegration,
	checkName: string
) => {
	switch (integrationName) {
		case bullhorn:
			try {
				const bhToken = await getBhRestToken(userIntegration);
				return createBullhornCertification(bhToken, checkName);
			} catch (error) {
				return null;
			}
		case jobAdder:
			return null; // TODO: change the certification Id for jobAdder
		default:
			return new Error('No integration found');
	}
};

export const getThirdPartyIntegration = async (integrationId: number) => {
	const customerId = conditionallyUpdateCacheAndGetSelectedSubCustomerId();
	const customerIntegrations = await getIntegrationsByCustomerId(customerId);
	const integration = customerIntegrations.find((x) => x.integrationId === integrationId);
	return integration;
};

export const getThirdPartyCandidateDetails = async (
	candidateId: string,
	integrationName: string,
	userIntegration: UserIntegration
) => {
	switch (integrationName) {
		case bullhorn:
			try {
				return await getBullhornCandidateDetails(candidateId, userIntegration);
			} catch (e) {
				if (hasValue(e.body)) throw new Error(`Failed to get data: ${e.body}`);
				return null;
			}
		case jobAdder:
			return getJobAdderCandidateDetails(candidateId, userIntegration);
		default:
			return null;
	}
};

// Bullhorn Integration

export interface BullhornData {
	data: BullhornCandidateData;
}
export interface BullhornCandidateData {
	firstName: string;
	lastName: string;
	middleName: string;
	id: number;
	dateOfBirth: string;
	mobile?: string;
	email: string;
}

export interface BullhornCredentials {
	userName?: string;
	password?: string;
}

export const getBhRestToken = async (bullhornIntegration: UserIntegration): Promise<BhToken> => {
	const response = await fetch(
		`${bullhornRestApiUrl}/rest-services/login?version=2.0&ttl=1440&access_token=${bullhornIntegration.accessToken}`,
		{ method: 'GET' }
	);
	if (
		response.status === statuses.unauthorized ||
		response.status === statuses.badRequest ||
		response.status === statuses.serverError
	) {
		// accessToken expired — refresh via backend (no credentials needed client-side)
		const updatedIntegration = await refreshBullhornToken(bullhornIntegration);
		return await getBhRestToken(updatedIntegration);
	}
	return response.json();
};

const refreshBullhornToken = async (bullhornIntegration: UserIntegration): Promise<UserIntegration> => {
	const tokens = await refreshIntegrationToken(
		bullhornIntegration.customerId,
		bullhornIntegration.integrationId,
		Number(bullhornIntegration.integrationUserID) || 0
	);
	return { ...bullhornIntegration, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken ?? '' };
};

const getBullhornCandidate = async (candidateId: string, bhToken: BhToken) => {
	const response = await fetch(
		`${bhToken.restUrl}entity/Candidate/${candidateId}?BhRestToken=${bhToken.BhRestToken}
		&fields=id,firstName,lastName,middleName,mobile,email`,
		{ method: 'GET' }
	);
	return response;
};

export const getBullhornCandidateDetails = async (candidateId: string, bullhornIntegration: UserIntegration) => {
	const bhToken = await getBhRestToken(bullhornIntegration);
	const candidate = await getBullhornCandidate(candidateId, bhToken);
	return await candidate.json();
};

export const createBullhornCertification = async (bhToken: BhToken, checkName: string) => {
	const response = await fetch(`${bhToken.restUrl}entity/Certification?BhRestToken=${bhToken.BhRestToken}`, {
		method: 'PUT',
		body: JSON.stringify({
			name: checkName,
			description: `${checkName} from VETTING.com`,
			expirationDateOptional: true
		})
	});
	return response.json();
};

// JobAdder Integration
export const generateJobAdderAccessTokenFromAuthCode = async (authCode: string, redirectUri: string) => {
	return await GetJobAdderAccessTokenFromAuthCode(authCode, redirectUri);
};

export const getJobAdderCandidateDetails = async (candidateId: string, jobAdderIntegration: UserIntegration) => {
	try {
		const candidateResponse = await getJobAdderCandidate(
			candidateId,
			jobAdderIntegration.integrationId,
			jobAdderIntegration.customerId
		);
		return candidateResponse;
	} catch (error) {
		return new Error(`An error occurred while fetching candidate details: ${error}`);
	}
};

export const getJobAdderApplicationDetails = async (applicationId: string, jobAdderIntegration: UserIntegration) => {
	const applicationResponse = await getJobAdderApplication(
		applicationId,
		jobAdderIntegration.integrationId,
		jobAdderIntegration.customerId
	);
	return applicationResponse;
};
